/**
 * Einkaufsliste des Haushalts mit Vorrat und Läden.
 *
 * Die Liste funktioniert auch ohne Netz: Abhaken und Hinzufügen landen in
 * einer Warteschlange und werden abgeglichen, sobald wieder Verbindung
 * besteht. Änderungen anderer Geräte erscheinen von selbst.
 */
import { categoryLabel } from '@cookbook/domain';
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ApiError, api } from '../api/client.ts';
import type { ShoppingItem, ShoppingList, Store } from '../api/types.ts';
import { useCachedQuery } from '../api/useCachedQuery.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { DragHandle } from '../components/DragHandle.tsx';
import { ScanDialog } from '../components/products/ScanDialog.tsx';
import { PantryPanel } from '../components/shopping/PantryPanel.tsx';
import { ShoppingItemRow } from '../components/shopping/ShoppingItemRow.tsx';
import { StoresPanel } from '../components/shopping/StoresPanel.tsx';
import {
  categoryOrder,
  formatPrice,
  packageHint,
  parseQuickAdd,
  sortWithinGroup,
} from '../lib/shoppingFormat.ts';
import { useDragReorder } from '../lib/useDragReorder.ts';
import { printPage } from '../lib/print.ts';
import { opTime, useShoppingList } from '../state/shoppingSync.ts';
import { texts } from '../texts/de.ts';

/** Gewählte Liste, damit sie beim nächsten Öffnen wieder erscheint. */
const SELECTED_LIST_KEY = 'kochbuch.einkauf.gewaehlt';

/** Bereiche der Seite. */
export type ShoppingTab = 'list' | 'pantry' | 'stores';

/** Reiter der Seite. */
function Tabs() {
  const tabs = [
    { to: '/einkauf', label: texts.shopping.tabList, end: true },
    { to: '/einkauf/vorrat', label: texts.shopping.tabPantry, end: false },
    { to: '/einkauf/laeden', label: texts.shopping.tabStores, end: false },
  ];
  return (
    <nav
      className="mb-4 flex gap-1 rounded-xl bg-surface-sunken p-1 text-sm"
      aria-label={texts.shopping.title}
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            `flex-1 rounded-lg px-3 py-1.5 text-center ${isActive ? 'bg-surface-raised font-semibold shadow-sm' : 'text-content-muted'}`
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Anzeige des Abgleichs: offline, wartende Änderungen oder abgeglichen. */
function SyncStatus({ online, pending }: { readonly online: boolean; readonly pending: number }) {
  if (!online) {
    return (
      <p role="status" className="mb-3 rounded-xl bg-accent-soft px-3 py-2 text-xs text-accent-strong">
        {texts.shopping.offline} {pending > 0 ? `(${pending})` : ''}
      </p>
    );
  }
  return (
    <p role="status" className="mb-3 text-right text-xs text-content-muted">
      {pending > 0 ? `${pending} ${texts.shopping.pending}` : texts.shopping.synced}
    </p>
  );
}

/** Die eigentliche Liste. */
function ListView({
  lists,
  setLists,
  stores,
}: {
  readonly lists: ShoppingList[];
  readonly setLists: (lists: ShoppingList[]) => void;
  readonly stores: Store[];
}) {
  const [selected, setSelected] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(SELECTED_LIST_KEY);
    } catch {
      return null;
    }
  });
  const listId = lists.some((list) => list.id === selected) ? selected : (lists[0]?.id ?? null);
  const { list, items, pending, online, loading, error, sync } = useShoppingList(listId);
  const [quickText, setQuickText] = useState('');
  const [scanning, setScanning] = useState(false);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    if (listId !== null) {
      try {
        window.localStorage.setItem(SELECTED_LIST_KEY, listId);
      } catch {
        // Ohne Speicher wird beim nächsten Mal die erste Liste gezeigt.
      }
    }
  }, [listId]);

  const store = stores.find((entry) => entry.id === list?.storeId);
  const open = items.filter((item) => !item.checked);
  const done = items.filter((item) => item.checked);
  const groups = useMemo(() => {
    const order = categoryOrder(store);
    const byCategory = new Map<string, ShoppingItem[]>();
    for (const item of open) {
      byCategory.set(item.category, [...(byCategory.get(item.category) ?? []), item]);
    }
    return [...byCategory.entries()]
      .sort(
        ([a], [b]) =>
          (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) -
          (order.indexOf(b) === -1 ? 99 : order.indexOf(b)),
      )
      .map(([category, entries]) => ({ category, entries: sortWithinGroup(entries) }));
  }, [open, store]);
  const byId = useMemo(() => new Map(open.map((item) => [item.id, item])), [open]);
  const drag = useDragReorder(
    useMemo(
      () => Object.fromEntries(groups.map((group) => [group.category, group.entries.map((item) => item.id)])),
      [groups],
    ),
    ({ id, group, groups: next }) =>
      sync?.enqueue({ type: 'move', id, category: group, order: next[group] ?? [], at: opTime() }),
  );
  const estimate = open.reduce((sum, item) => sum + (packageHint(item)?.priceCents ?? 0), 0);

  /** Neue Liste anlegen. */
  async function createList() {
    const name = window.prompt(texts.shopping.newListPrompt);
    if (name === null || name.trim() === '') {
      return;
    }
    try {
      const result = await api.post<{ list: ShoppingList }>('/api/shopping/lists', { name });
      setLists([...lists, result.list]);
      setSelected(result.list.id);
    } catch (caught) {
      window.alert(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  /** Benennt die gewählte Liste um. */
  async function renameList() {
    if (list === null) {
      return;
    }
    const name = window.prompt(texts.shopping.renameListPrompt, list.name);
    if (name === null || name.trim() === '' || name.trim() === list.name) {
      return;
    }
    try {
      const result = await api.put<{ list: ShoppingList }>(`/api/shopping/lists/${list.id}`, { name });
      setLists(lists.map((entry) => (entry.id === result.list.id ? result.list : entry)));
      void sync?.refresh();
    } catch (caught) {
      window.alert(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  /** Löscht die gewählte Liste; die letzte Liste bleibt. */
  async function deleteList() {
    if (list === null || lists.length <= 1 || !window.confirm(texts.shopping.deleteListConfirm)) {
      return;
    }
    try {
      await api.delete(`/api/shopping/lists/${list.id}`);
      const rest = lists.filter((entry) => entry.id !== list.id);
      setLists(rest);
      setSelected(rest[0]?.id ?? null);
    } catch (caught) {
      window.alert(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  /** Laden für die Sortierung wählen. */
  async function chooseStore(storeId: string) {
    if (list === null) {
      return;
    }
    try {
      const result = await api.put<{ list: ShoppingList }>(`/api/shopping/lists/${list.id}`, {
        storeId: storeId === '' ? null : storeId,
      });
      setLists(lists.map((entry) => (entry.id === result.list.id ? result.list : entry)));
      void sync?.refresh();
    } catch (caught) {
      window.alert(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  return (
    <>
      {/* Auf Papier: nur der Name der Liste und das Datum. */}
      <p className="mb-3 hidden text-lg font-semibold print:block">
        {list?.name ?? texts.shopping.title} · {new Date().toLocaleDateString('de-DE')}
      </p>
      <div className="mb-3 flex flex-wrap items-center gap-2 print:hidden">
        <label className="flex items-center gap-2 text-sm">
          <span className="sr-only">{texts.shopping.list}</span>
          <select
            value={listId ?? ''}
            onChange={(event) => setSelected(event.target.value)}
            className="rounded-xl border border-border-subtle bg-surface-raised px-3 py-2 font-semibold"
          >
            {lists.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void createList()}
          className="rounded-xl border border-border-subtle px-3 py-2 text-sm"
        >
          + {texts.shopping.newList}
        </button>
        <button
          type="button"
          onClick={() => void renameList()}
          disabled={list === null}
          className="rounded-xl border border-border-subtle px-3 py-2 text-sm disabled:opacity-50"
        >
          {texts.shopping.renameList}
        </button>
        {lists.length > 1 ? (
          <button
            type="button"
            onClick={() => void deleteList()}
            className="rounded-xl border border-border-subtle px-3 py-2 text-sm text-content-muted"
          >
            {texts.shopping.deleteList}
          </button>
        ) : null}
        <button
          type="button"
          onClick={printPage}
          disabled={items.length === 0}
          className="rounded-xl border border-border-subtle px-3 py-2 text-sm disabled:opacity-50"
        >
          {texts.print.print}
        </button>
        {stores.length === 0 ? null : (
          <label className="ml-auto flex items-center gap-2 text-sm">
            <span className="text-content-muted">{texts.shopping.sortBy}</span>
            <select
              value={list?.storeId ?? ''}
              onChange={(event) => void chooseStore(event.target.value)}
              className="rounded-xl border border-border-subtle bg-surface-raised px-2 py-1.5"
            >
              <option value="">{texts.shopping.sortCategories}</option>
              {stores.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="print:hidden">
        <SyncStatus online={online} pending={pending} />
      </div>
      {error === null ? null : (
        <p className="mb-3 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{error}</p>
      )}

      <form
        className="mb-4 flex gap-2 print:hidden"
        onSubmit={(event) => {
          event.preventDefault();
          const parsed = parseQuickAdd(quickText);
          if (parsed === null || sync === null) {
            return;
          }
          sync.enqueue({ type: 'add', id: crypto.randomUUID(), ...parsed, at: opTime() });
          setQuickText('');
        }}
      >
        <input
          value={quickText}
          onChange={(event) => setQuickText(event.target.value)}
          placeholder={texts.shopping.quickAddPlaceholder}
          aria-label={texts.shopping.quickAdd}
          className="flex-1 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2.5 outline-none focus:border-accent"
        />
        <button
          type="submit"
          disabled={quickText.trim() === ''}
          className="rounded-xl bg-accent px-4 font-semibold text-on-accent disabled:opacity-50"
        >
          {texts.shopping.quickAdd}
        </button>
        <button
          type="button"
          onClick={() => setScanning(true)}
          aria-label={texts.scan.title}
          title={texts.scan.title}
          className="rounded-xl border border-border-subtle bg-surface-raised px-3 text-content-muted"
        >
          <AppIcon name="barcode" size={22} />
        </button>
      </form>
      {scanning ? (
        <ScanDialog
          onClose={() => setScanning(false)}
          onAddToList={(product) =>
            sync?.enqueue({
              type: 'add',
              id: crypto.randomUUID(),
              name: product.name,
              amount: null,
              unit: '',
              note: null,
              category: product.category,
              at: opTime(),
            })
          }
        />
      ) : null}

      {loading && items.length === 0 ? (
        <p className="text-sm text-content-muted">{texts.common.loading}</p>
      ) : null}
      {!loading && items.length === 0 ? (
        <p className="rounded-card border border-border-subtle bg-surface-raised p-6 text-center text-sm text-content-muted">
          {texts.shopping.empty}
        </p>
      ) : null}

      <div className="space-y-3">
        {groups.map((group) => (
          <section
            key={group.category}
            {...drag.containerProps(group.category)}
            className="rounded-card border border-border-subtle bg-surface-raised px-3 py-2 print:break-inside-avoid print:border-0 print:p-0"
          >
            <h2 className="pt-1 text-xs font-semibold tracking-wide text-accent uppercase">
              {categoryLabel(group.category)}
            </h2>
            <ul className="min-h-10 divide-y divide-border-subtle">
              {(drag.groups[group.category] ?? [])
                .map((id) => byId.get(id))
                .filter((item) => item !== undefined)
                .map((item) => (
                  <ShoppingItemRow
                    key={item.id}
                    item={item}
                    sortProps={drag.itemProps(item.id, group.category)}
                    dragging={drag.dragging === item.id}
                    handle={<DragHandle label={item.name} {...drag.handleProps(item.id)} />}
                    onToggle={() =>
                      sync?.enqueue({ type: 'check', id: item.id, checked: true, at: opTime() })
                    }
                    onChange={(changes) =>
                      sync?.enqueue({ type: 'update', id: item.id, ...changes, at: opTime() })
                    }
                    onRemove={() => sync?.enqueue({ type: 'delete', id: item.id, at: opTime() })}
                  />
                ))}
            </ul>
          </section>
        ))}
      </div>

      {estimate > 0 ? (
        <p className="mt-3 text-right text-sm text-content-muted">
          {texts.shopping.estimate}: <strong className="text-content">{formatPrice(estimate)}</strong>
        </p>
      ) : null}

      {done.length === 0 ? null : (
        <section className="mt-5 rounded-card border border-border-subtle bg-surface-raised px-3 py-2 print:hidden">
          <button
            type="button"
            onClick={() => setShowCart((value) => !value)}
            aria-expanded={showCart}
            className="flex w-full items-center justify-between py-1 text-sm font-semibold"
          >
            <span className="inline-flex items-center gap-2">
              <AppIcon name="cart" size={18} />
              {texts.shopping.inCart} ({done.length})
            </span>
            <span className="text-content-muted">{showCart ? '▲' : '▼'}</span>
          </button>
          {showCart ? (
            <ul className="divide-y divide-border-subtle">
              {done.map((item) => (
                <ShoppingItemRow
                  key={item.id}
                  item={item}
                  onToggle={() => sync?.enqueue({ type: 'check', id: item.id, checked: false, at: opTime() })}
                  onChange={(changes) =>
                    sync?.enqueue({ type: 'update', id: item.id, ...changes, at: opTime() })
                  }
                  onRemove={() => sync?.enqueue({ type: 'delete', id: item.id, at: opTime() })}
                />
              ))}
            </ul>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2 pb-1">
            <button
              type="button"
              onClick={() =>
                sync?.enqueue({
                  type: 'clearChecked',
                  ids: done.map((item) => item.id),
                  toPantry: false,
                  at: opTime(),
                })
              }
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm"
            >
              {texts.shopping.clearChecked}
            </button>
            <button
              type="button"
              onClick={() =>
                sync?.enqueue({
                  type: 'clearChecked',
                  ids: done.map((item) => item.id),
                  toPantry: true,
                  at: opTime(),
                })
              }
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm"
            >
              {texts.shopping.clearToPantry}
            </button>
          </div>
        </section>
      )}
    </>
  );
}

/** Seite der Einkaufsliste. */
export function ShoppingPage({ tab = 'list' }: { readonly tab?: ShoppingTab }) {
  const lists = useCachedQuery<{ lists: ShoppingList[] }>('/api/shopping/lists', 'einkauf.listen');
  const stores = useCachedQuery<{ stores: Store[] }>('/api/stores', 'einkauf.laeden');

  return (
    <section className="mx-auto max-w-3xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight print:hidden">{texts.shopping.title}</h1>
        <p className="mt-0.5 text-sm text-content-muted print:hidden">{texts.shopping.subtitle}</p>
      </header>
      <div className="print:hidden">
        <Tabs />
      </div>
      {tab === 'list' ? (
        lists.data === null ? (
          <p className="text-sm text-content-muted">
            {lists.error ?? (lists.offline ? texts.shopping.offline : texts.common.loading)}
          </p>
        ) : (
          <ListView
            lists={lists.data.lists}
            setLists={(next) => lists.setData({ lists: next })}
            stores={stores.data?.stores ?? []}
          />
        )
      ) : null}
      {tab === 'pantry' ? <PantryPanel /> : null}
      {tab === 'stores' ? (
        <StoresPanel
          stores={stores.data?.stores ?? []}
          onChange={(next) => stores.setData({ stores: next })}
        />
      ) : null}
    </section>
  );
}
